import { useFormatNumber } from "~/composables/useUtils";
import type { ReceiptItemCustomSection } from "~/interface/ReceiptOrder";

// A row printed inside a Custom block. `value` is passed verbatim — the caller
// is responsible for its formatting and sign (see buildDiscountSection).
export interface CustomRow {
  name: string;
  value: string;
}

// Layout flags for a Custom block. All optional; each defaults to the "off"
// value in buildCustomSection so callers only set what they need.
export interface CustomSectionOptions {
  Underline?: boolean;
  DoubleUnderline?: boolean;
  Bold?: boolean;
  Italic?: boolean;
  Indent?: number;
}

/**
 * Builder for the `Custom` print block (extra rows printed under a line item).
 *
 * Kept generic and free of any domain logic so it can be reused across print
 * surfaces — the customer receipt today, and the kitchen slip in the future.
 * The block shape matches ReceiptItemCustomSection, which the print service
 * renders with the same Rows/Underline/Bold logic as the Summary sections.
 */
export function usePrintCustomSection() {
  /**
   * Generic section builder — rows are printed verbatim and every layout flag
   * defaults to off. This function knows nothing about money, signs or discounts.
   */
  function buildCustomSection(
    rows: CustomRow[],
    options: CustomSectionOptions = {}
  ): ReceiptItemCustomSection {
    return {
      Rows: rows,
      Underline: options.Underline ?? false,
      DoubleUnderline: options.DoubleUnderline ?? false,
      Bold: options.Bold ?? false,
      Italic: options.Italic ?? false,
      Indent: options.Indent ?? 0,
    };
  }

  /**
   * Convenience builder for a discount row, e.g. { name: "ลด 15%", value: "-90.00" }.
   *
   * The minus sign is NOT hardcoded — it comes from formatting a negative amount,
   * so a positive `discountAmount` naturally yields a negative money string. Pass
   * `options` to override the indent/flags per print surface.
   */
  function buildDiscountSection(
    discountAmount: number,
    discountLabel: string,
    options: CustomSectionOptions = {}
  ): ReceiptItemCustomSection {
    const money = useFormatNumber(-Math.abs(discountAmount || 0), 2, true) as string;
    return buildCustomSection(
      [{ name: discountLabel || "ส่วนลด", value: money }],
      { Indent: 10, ...options }
    );
  }

  return { buildCustomSection, buildDiscountSection };
}
